import { formatConsumptionReportCost } from "@/pages/utility/report/utils/reportGrid";
import { getMomentTz } from "@dexteel/mesf-core";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import { Box, Grid2, Paper, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import type {
	UsageMeterData,
	WaterMeterReading,
	WaterUsageSummary,
} from "../models/MillOverview2";

const moment = getMomentTz();

type Props = {
	data: UsageMeterData;
};

const formatNumber = (value: number): string => {
	return value.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

const formatRatio = (value: number | null): string =>
	value == null
		? "-"
		: value.toLocaleString("en-US", { maximumFractionDigits: 1 });

const normalizeEmpty = (value: string): string =>
	value.trim() === "" || value.trim() === "-" ? "-" : value;

type WaterKpiRowProps = {
	label: string;
	value: string;
	isOverScale?: boolean;
};

const WaterKpiRow = ({
	label,
	value,
	isOverScale = false,
}: WaterKpiRowProps) => {
	const displayValue = normalizeEmpty(value);

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "baseline",
				justifyContent: "space-between",
				gap: 0.5,
				py: 0.15,
			}}
		>
			<Typography
				sx={{
					fontSize: "0.7rem",
					fontWeight: 500,
				}}
			>
				{label}
			</Typography>

			<Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
				{isOverScale && (
					<NorthEastIcon
						sx={{ fontSize: "0.75rem" }}
						color="warning"
						titleAccess="Above display max"
					/>
				)}
				<Typography
					sx={{
						fontSize: "0.7rem",
						fontVariantNumeric: "tabular-nums",
						lineHeight: 1,
					}}
				>
					{displayValue}
				</Typography>
			</Box>
		</Box>
	);
};

const WaterUsageSummaryCard = ({ summary }: { summary: WaterUsageSummary }) => {
	const isUnavailable = !summary.IsAvailable;

	return (
		<Paper
			elevation={1}
			sx={{
				p: 1,
				backgroundColor: "#FAFAFA",
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 0.5,
					mb: 0.75,
				}}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					<WaterDropOutlinedIcon
						sx={{ fontSize: "0.85rem", color: "#0277BD" }}
					/>
					<Typography
						sx={{
							color: "#3a3a3a",
							fontSize: "0.75rem",
							fontWeight: 800,
							letterSpacing: "0.025em",
						}}
					>
						Water Usage
					</Typography>
				</Box>

				<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
					{isUnavailable && (
						<Typography
							sx={{
								color: "#854F0B",
								backgroundColor: "#FAEEDA",
								fontSize: "0.6rem",
								fontWeight: 700,
								lineHeight: 1,
								px: 0.6,
								py: 0.35,
								borderRadius: 0.75,
							}}
						>
							Unav.
						</Typography>
					)}
					<Typography
						sx={{
							color: "#3a3a3a",
							fontSize: "0.7rem",
							fontWeight: 700,
						}}
					>
						{summary.WindowHours}H
					</Typography>
				</Box>
			</Box>

			<Box sx={{ display: "grid", gap: 0.1, flexGrow: 1 }}>
				<WaterKpiRow
					label="Avg Gal / Hour"
					value={
						summary.AverageGallonsPerHour == null
							? "-"
							: formatNumber(summary.AverageGallonsPerHour)
					}
				/>
				<WaterKpiRow
					label="Gallons / Ton"
					value={formatRatio(summary.GallonsPerTon)}
					isOverScale={
						summary.GallonsPerTon != null &&
						summary.GallonsPerTon > summary.GallonsPerTonDisplayMax
					}
				/>
				<WaterKpiRow
					label="Gallons / Billet"
					value={formatRatio(summary.GallonsPerBillet)}
					isOverScale={
						summary.GallonsPerBillet != null &&
						summary.GallonsPerBillet > summary.GallonsPerBilletDisplayMax
					}
				/>
				<WaterKpiRow
					label="$ / Ton"
					value={formatConsumptionReportCost(
						summary.CostPerTon,
						summary.IsCostApproximate,
					)}
				/>
				<WaterKpiRow
					label="$ / Billet"
					value={formatConsumptionReportCost(
						summary.CostPerBillet,
						summary.IsCostApproximate,
					)}
				/>
			</Box>

			<Typography
				sx={{
					color: "text.secondary",
					fontSize: "0.65rem",
					mt: 0.65,
					pt: 0.5,
					borderTop: "1px solid rgba(0, 0, 0, 0.08)",
					lineHeight: 1.2,
				}}
			>
				{summary.WindowHours}H through{" "}
				{summary.DataThrough
					? moment(summary.DataThrough).format("MM/DD h:mm A")
					: "-"}
			</Typography>
		</Paper>
	);
};

const WaterUsageChart = ({ meters }: { meters: WaterMeterReading[] }) => {
	const totalGallons = meters.reduce((sum, m) => sum + (m.Gallons ?? 0), 0);
	const chartOptions = useMemo<EChartsOption>(() => {
		const fixedMeters = meters.toSorted((a, b) =>
			a.ExternalMeterId.localeCompare(b.ExternalMeterId),
		);
		const maxGallons = Math.max(
			...fixedMeters.map((meter) => meter.Gallons ?? 0),
			1,
		);
		const colors = ["#0277BD", "#039BE5", "#4FC3F7"];
		const colorByMeterId = new Map(
			meters
				.toSorted((a, b) => (b.Gallons ?? 0) - (a.Gallons ?? 0))
				.map((meter, index) => [
					meter.ExternalMeterId,
					colors[index % colors.length],
				]),
		);

		return {
			animationDuration: 500,
			tooltip: {
				trigger: "item",
				confine: true,
			},
			grid: {
				left: 70,
				right: 28,
				top: 4,
				bottom: 4,
			},
			xAxis: {
				type: "value",
				min: 0,
				max: maxGallons * 1.35,
				show: false,
			},
			yAxis: {
				type: "category",
				data: fixedMeters.map((meter) => meter.ExternalMeterId),
				inverse: true,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: {
					margin: 4,
					formatter: (_value: string, index: number) => {
						const meter = fixedMeters[index];
						if (!meter) return "";

						const lastUpdate = meter.LastUpdate
							? moment(meter.LastUpdate).format("MM/DD HH:mm")
							: "No update";

						return `{meter|${meter.ExternalMeterId}}\n{time|${lastUpdate}}`;
					},
					rich: {
						meter: {
							color: "#263238",
							fontSize: 11,
							fontWeight: 700,
							lineHeight: 15,
						},
						time: {
							color: "#263238",
							fontSize: 11,
							fontWeight: 400,
							lineHeight: 15,
						},
					},
				},
			},
			series: [
				{
					type: "bar",
					data: fixedMeters.map((meter) => {
						const gallons =
							meter.Gallons != null
								? formatNumber(meter.Gallons)
								: "No reading";
						const lastUpdate = meter.LastUpdate
							? moment(meter.LastUpdate).format("MM/DD/YYYY HH:mm")
							: "No reading";

						return {
							value: meter.Gallons ?? 0,
							itemStyle: {
								color: colorByMeterId.get(meter.ExternalMeterId),
							},
							tooltip: {
								formatter: `<strong>Meter ${meter.ExternalMeterId}</strong><br/>${gallons} gallons<br/>Last update: ${lastUpdate}`,
							},
						};
					}),
					barWidth: 16,
					barMinHeight: 4,
					itemStyle: {
						borderRadius: [0, 6, 6, 0],
					},
					label: {
						show: true,
						position: "right",
						color: "#263238",
						fontSize: 11,
						fontWeight: 700,
						formatter: (params: { dataIndex?: number }) => {
							const gallons = fixedMeters[params.dataIndex ?? 0]?.Gallons;
							return gallons != null ? `${formatNumber(gallons)}g` : "-";
						},
					},
				},
			],
		};
	}, [meters]);

	return (
		<Paper
			elevation={1}
			sx={{
				p: 1,
				backgroundColor: "#FAFAFA",
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography
				variant="caption"
				sx={{
					fontWeight: 700,
					fontSize: "0.75rem",
					display: "block",
					mb: 0.5,
					textAlign: "center",
				}}
			>
				Water Usage: {formatNumber(totalGallons)} Gallons
			</Typography>

			<Box sx={{ flexGrow: 1, minHeight: 90 }}>
				{meters.length === 0 ? (
					<Typography
						variant="caption"
						sx={{
							fontSize: "0.7rem",
							color: "text.secondary",
							display: "block",
							textAlign: "center",
							mt: 2,
						}}
					>
						No meters configured
					</Typography>
				) : (
					<ReactECharts
						option={chartOptions}
						notMerge
						lazyUpdate
						style={{ height: "100%", minHeight: 90, width: "100%" }}
						opts={{ renderer: "svg" }}
					/>
				)}
			</Box>
		</Paper>
	);
};

type MeterProps = {
	title: string;
	value: number;
	target: number;
	unit: string;
	color: string;
};

const UsageMeter = ({ title, value, target, unit, color }: MeterProps) => {
	const percentage = Math.min((value / target) * 100, 100);
	const barCount = 5;
	const filledBars = Math.round((percentage / 100) * barCount);

	return (
		<Paper
			elevation={1}
			sx={{
				p: 1,
				textAlign: "center",
				backgroundColor: "#FAFAFA",
				height: "100%",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Typography
				variant="caption"
				sx={{
					fontWeight: 600,
					fontSize: "0.65rem",
					display: "block",
					mb: 0.5,
				}}
			>
				{title}: {formatNumber(value)} {unit}
			</Typography>

			<Box
				sx={{
					flexGrow: 1,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					gap: 0.5,
					minHeight: 60,
				}}
			>
				{Array.from({ length: barCount }).map((_, index) => (
					<Box
						key={index}
						sx={{
							width: 12,
							height: "80%",
							backgroundColor: index < filledBars ? color : "#E0E0E0",
							borderRadius: 0.5,
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-end",
						}}
					>
						{index < filledBars && (
							<Box
								sx={{
									height: `${100 - (index / filledBars) * 30}%`,
									backgroundColor: color,
									borderRadius: 0.5,
								}}
							/>
						)}
					</Box>
				))}
			</Box>

			<Box>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						fontSize: "0.55rem",
						color: "#666",
					}}
				>
					<Typography variant="caption" sx={{ fontSize: "0.55rem" }}>
						Gal/Ton
					</Typography>
					<Typography variant="caption" sx={{ fontSize: "0.55rem" }}>
						S/Ton
					</Typography>
					<Typography variant="caption" sx={{ fontSize: "0.55rem" }}>
						S/Billet
					</Typography>
				</Box>
				<Typography
					variant="caption"
					sx={{ fontSize: "0.55rem", color: "#666", display: "block" }}
				>
					kwh/Ton kwh/Billet
				</Typography>
				<Typography
					variant="caption"
					sx={{ fontSize: "0.55rem", color: "#666", display: "block" }}
				>
					Current Rolling
				</Typography>
			</Box>
		</Paper>
	);
};

export const UsageMeters = ({ data }: Props) => {
	return (
		<Grid2 container spacing={1} sx={{ height: "100%" }}>
			<Grid2 size={{ xs: 4 }} sx={{ display: "flex" }}>
				{data.WaterSummary ? (
					<WaterUsageSummaryCard summary={data.WaterSummary} />
				) : (
					<WaterUsageChart meters={data.WaterMeters ?? []} />
				)}
			</Grid2>
			<Grid2 size={{ xs: 4 }} sx={{ display: "flex" }}>
				<UsageMeter
					title="Electricity Usage"
					value={data.ElectricityUsage}
					target={data.ElectricityTarget}
					unit="kwh"
					color="#FF9800"
				/>
			</Grid2>
			<Grid2 size={{ xs: 4 }} sx={{ display: "flex" }}>
				<UsageMeter
					title="Gas Usage"
					value={data.GasUsage}
					target={data.GasTarget}
					unit="mmBtu"
					color="#9C27B0"
				/>
			</Grid2>
		</Grid2>
	);
};
